import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

/**
 * ScrollLockReleaseGuard — a defensive safety net for the "phantom
 * scroll-lock" bug.
 *
 * Background
 * ----------
 * Multiple components in the app use Radix Dialog / Sheet / Drawer
 * variants (which internally use `react-remove-scroll`). When a
 * dialog opens, react-remove-scroll sets `overflow: hidden` (plus
 * a few related properties) on `<body>` and tracks open dialogs via
 * a global ref-count. When the count returns to 0, it cleans up.
 *
 * In practice, the count can leak to a non-zero stuck value when:
 *   • the language is switched WHILE a dialog is open (the entire
 *     subtree re-renders with new `dir`, dialog unmounts + remounts
 *     out of order with the ref-count cleanup);
 *   • a dialog's parent unmounts mid-transition (e.g. user navigates
 *     via React Router Link while a dialog is open);
 *   • two dialogs open concurrently and one closes a frame before the
 *     other registers — the counter sees +1, +1, -1 and stays at 1.
 *
 * Symptom: the page becomes un-scrollable "randomly" with no obvious
 * trigger. The user sees a frozen body.
 *
 * Fix
 * ---
 * On EVERY route change AND on EVERY language change, we forcibly
 * clear the body styles that `react-remove-scroll` and Radix dialogs
 * set. This is a *circuit breaker*:
 *   • if Radix is in a clean state, it's a no-op (those properties
 *     are already empty);
 *   • if Radix leaked, it self-heals within one navigation or
 *     language toggle — the user just needs to tap a nav link OR
 *     switch the language and back, and scroll comes back.
 *
 * We intentionally do NOT clear on every render — only on
 * navigation/language transitions, so we never interrupt an actively
 * open dialog mid-interaction.
 */
export default function ScrollLockReleaseGuard() {
  const location = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    if (!body || !html) return;
    // react-remove-scroll sets these — clear them defensively.
    body.style.removeProperty("overflow");
    body.style.removeProperty("padding-right");
    body.style.removeProperty("pointer-events");
    body.style.removeProperty("position");
    body.style.removeProperty("top");
    body.style.removeProperty("touch-action");
    html.style.removeProperty("overflow");
    // Radix attaches data-scroll-locked while any dialog is open.
    // If we're navigating / switching language, by definition no
    // dialog should be holding state hostage, so clear the marker.
    body.removeAttribute("data-scroll-locked");
    // Also clear any class the library may have added (it doesn't
    // currently, but future versions might).
    body.classList.forEach((c) => {
      if (c.startsWith("rrs-")) body.classList.remove(c);
    });
  }, [location.pathname, location.search, lang]);

  return null;
}
